"use client";

/* eslint-disable @next/next/no-img-element -- static Figma vectors at their exact intrinsic sizes */

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { prefersReducedMotion } from "@/components/trademania-2/shared";

import aiLogo from "@/public/trademania-2/t-ai-logo.svg";
import idea from "@/public/trademania-2/t-idea.svg";
import eye from "@/public/trademania-2/t-eye.svg";
import chatAdd from "@/public/trademania-2/chat-add.svg";
import chatMic from "@/public/trademania-2/chat-mic.svg";
import chatArrow from "@/public/trademania-2/chat-arrow.svg";
import chatIdea from "@/public/trademania-2/chat-idea.svg";
import chatLogo from "@/public/trademania-2/chat-logo.svg";

/* ------------------------------------------------------------------ motion */

/** Steps 0..count-1 forever; stays on `initial` (the Figma state) with reduced motion. */
function useCycle(count: number, ms: number, initial = 0) {
  const [index, setIndex] = useState(initial);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), ms);
    return () => window.clearInterval(id);
  }, [count, ms]);
  return index;
}

/** Eases the returned number towards `target` whenever it changes. */
function useTween(target: number, ms = 900) {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    // with reduced motion the cycles never advance, so the target never changes
    if (prefersReducedMotion()) return;
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const v = a + (target - a) * (1 - Math.pow(1 - t, 3));
      from.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return value;
}

const thousands = (n: number, sep: string) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep);
const signed = (n: number, digits = 2) => `${n >= 0 ? "+" : "-"}${Math.abs(n).toFixed(digits)}`;

const Svg = ({ src, w, h = w, className }: { src: { src: string }; w: number; h?: number; className?: string }) => (
  <img src={src.src} alt="" width={w} height={h} className={cn("block shrink-0", className)} style={{ width: w, height: h }} />
);

const square = "tm2-anim relative h-[206px] w-[206px] overflow-clip font-geist";

/* ----------------------------------------------------------------- candles */

// [x, wickTop, wickBottom, bodyTop, bodyBottom, up] on the 182px-wide Figma chart
type Candle = [number, number, number, number, number, 0 | 1];

const BTC_CANDLES: Candle[] = [
  [3.3, 39, 73.9, 54.1, 69.2, 0], [9.8, 57.2, 86.1, 69.1, 77.3, 0], [16.3, 65, 94, 77.3, 81.6, 0],
  [22.8, 67.7, 92, 78.9, 81.6, 1], [29.3, 63.2, 85, 67.2, 78.9, 1], [35.8, 47.6, 68.1, 59.1, 67.2, 1],
  [42.3, 44, 74.2, 51.7, 59, 1], [48.8, 38.3, 60.1, 40.3, 51.7, 1], [55.3, 13.8, 50.9, 25.1, 40.3, 1],
  [61.8, 6.3, 40, 21.3, 25.2, 1], [68.3, 15.8, 37.9, 21.3, 24.2, 0], [74.8, 21.6, 33.2, 24.1, 28.3, 0],
  [81.3, 23.5, 44.7, 28.3, 29.3, 0], [87.8, 14.2, 43.8, 26.2, 29.4, 1], [94.3, 21.7, 48.5, 26.2, 39.4, 0],
  [100.8, 30.8, 42.5, 37.7, 39.3, 1], [107.3, 22.1, 43.7, 23, 37.7, 1], [113.8, 12.1, 42, 23, 26.8, 0],
  [120.3, 22.3, 33.5, 25, 26.8, 1], [126.8, 19, 37.8, 25, 30.8, 0], [133.3, 22.1, 45.1, 30.8, 35, 0],
  [139.8, 20.1, 49.9, 35, 42.4, 0], [146.3, 18.8, 52.8, 29.2, 42.4, 1], [152.8, 19.1, 40.5, 27.4, 29.2, 1],
  [159.3, 5.1, 39, 11.6, 27.3, 1], [165.8, 2, 24.2, 11.6, 18.5, 0], [172.3, 12.1, 35.8, 18.5, 22.7, 0],
  [178.8, 7.2, 36.2, 9, 22.7, 1],
];

const TRADE_CANDLES: Candle[] = [
  [4.1, 23.3, 40.9, 31.6, 37.9, 0], [12.4, 25.7, 43.2, 37.9, 38.9, 0], [20.7, 23.2, 42.9, 32.7, 37.9, 1],
  [29, 32, 36.6, 32.7, 33.8, 0], [37.2, 13.3, 35.7, 25.3, 33.8, 1], [45.5, 7.6, 26.8, 13.9, 25.3, 1],
  [53.8, 6.5, 22.1, 13.9, 20, 0], [62, 16.6, 37.3, 20, 29.4, 0], [70.3, 21.1, 41.5, 27, 29.4, 1],
  [78.6, 21.9, 40.3, 27, 36.4, 0], [86.9, 36.4, 52.7, 36.4, 47.6, 0], [95.1, 44.7, 68, 47.6, 56.6, 0],
  [103.4, 39.5, 62, 48.9, 56.7, 1], [111.7, 27.4, 58.7, 39.2, 48.9, 1], [120, 27.2, 43.5, 34.2, 39.2, 1],
  [128.2, 28.1, 41.6, 34.2, 35.5, 0], [136.5, 24, 43.7, 32.9, 35.5, 1], [144.8, 19.7, 43.8, 28.7, 32.9, 1],
  [153, 7.1, 34, 19.2, 28.6, 1], [161.3, 6.8, 31.2, 18.5, 19.5, 1], [169.6, 17.6, 25.3, 18.5, 21, 0],
  [177.9, 2, 30.1, 10.7, 21.1, 1],
];

/** Candles print one after the other, hold, fade and start again; the last one keeps ticking. */
function Candles({
  candles,
  height,
  grid,
  gridColor,
  step = 70,
}: {
  candles: Candle[];
  height: number;
  grid: number[];
  gridColor: string;
  step?: number;
}) {
  return (
    <svg width={182} height={height} viewBox={`0 0 182 ${height}`} className="block shrink-0 overflow-visible">
      {grid.map((y) => (
        <path key={y} d={`M0 ${y}H182`} stroke={gridColor} strokeWidth={1} />
      ))}
      {candles.map(([x, w1, w2, b1, b2, up], i) => {
        const color = up ? "#3BE07A" : "#FF5C6C";
        const last = i === candles.length - 1;
        return (
          <g key={i} className="tm2-candle" style={{ animationDelay: `${i * step}ms` }}>
            <path d={`M${x} ${w1}V${w2}`} stroke={color} strokeWidth={1} />
            <rect
              x={x - 2.1}
              y={b1}
              width={4.2}
              height={Math.max(1, b2 - b1)}
              fill={color}
              className={last ? "tm2-candle-live" : undefined}
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------- tiles */

const BTC_PRICES = [64688, 64731, 64654, 64702, 64776, 64719];
const BTC_OPEN = 64437;

function BtcUsdChart() {
  const price = useTween(BTC_PRICES[useCycle(BTC_PRICES.length, 1700)], 700);
  const change = (price / BTC_OPEN - 1) * 100;

  return (
    <div className={cn(square, "flex flex-col gap-[10px] rounded-[15px] bg-white px-[12px] pb-[12px] pt-[14px]")}>
      <div className="flex h-[20px] w-[182px] items-center justify-between whitespace-nowrap text-[14px] font-medium leading-[18px]">
        <p className="text-[#0B0C0D]">BTC/USD</p>
        <p className="text-[#0B0C0D]/50">4h</p>
      </div>
      <div className="flex h-[29px] items-center gap-[8px]">
        <p className="whitespace-nowrap text-[23px] font-semibold leading-none tracking-[-.02em] tabular-nums text-[#0B0C0D]">
          {thousands(price, ",")}
        </p>
        <span className="tm2-trim-start tm2-trim-end rounded-[4px] bg-[#111312] px-[6px] pt-[5px] pb-[9px] text-[14px] font-medium leading-[18px] tabular-nums text-[#9BE055]">
          {signed(change)}%
        </span>
      </div>
      <Candles candles={BTC_CANDLES} height={96} grid={[24, 48, 72]} gridColor="rgba(0,0,0,.06)" />
    </div>
  );
}

const AI_PROMPTS = ["Analysiere den Chart", "Zeig mir meinen Vorteil", "Analysiere die Indikatoren"];

function AiChat() {
  const active = useCycle(AI_PROMPTS.length, 1800);

  return (
    <div className={cn(square, "rounded-[14.892px] bg-white")}>
      <div className="absolute left-1/2 top-[11px] flex w-[138px] -translate-x-1/2 flex-col items-center gap-[10px]">
        <span className="relative block h-[44px] w-[44px]">
          <span className="tm2-ping absolute inset-0 rounded-full bg-[#9BE055]/40" />
          <Svg src={aiLogo} w={44} className="tm2-breathe relative" />
        </span>
        <p className="whitespace-nowrap text-[16px] font-medium tracking-[-.01em] text-[#111312]">Willkommen Julian</p>
      </div>
      <div className="absolute left-[16px] top-[95px] flex w-[174px] flex-col gap-[5px]">
        {AI_PROMPTS.map((text, i) => (
          <div
            key={text}
            className={cn(
              "flex w-full items-center justify-center gap-[6.347px] rounded-[3px] border-[.529px] px-[8.463px] py-[6.347px] transition-all duration-500 ease-[cubic-bezier(.22,1,.36,1)]",
              i === active ? "scale-[1.03] border-[#111312] bg-[#111312] text-white" : "border-black/[.05] bg-[#111312]/5 text-black",
            )}
          >
            <Svg src={idea} w={10.579} className={cn("transition-[filter] duration-500", i === active && "invert")} />
            <p className="whitespace-nowrap text-[12px] leading-[14px] opacity-80">{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const WIN_RATES = [40, 56, 47, 63];
const RING = 141;
const RING_STROKE = 4.9;
const RING_R = (RING - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

function WinRate() {
  const rate = useTween(WIN_RATES[useCycle(WIN_RATES.length, 2600)], 1200);

  return (
    <div className={cn(square, "rounded-[14.892px] bg-[#F3E9D7]")}>
      {/* starts at 12 o'clock and runs counter-clockwise, as in Figma */}
      <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} className="absolute left-[32.5px] top-[32.5px] -scale-x-100">
        <circle cx={RING / 2} cy={RING / 2} r={RING_R} fill="none" stroke="#111312" strokeOpacity={0.12} strokeWidth={RING_STROKE} />
        <circle
          cx={RING / 2}
          cy={RING / 2}
          r={RING_R}
          fill="none"
          stroke="#111312"
          strokeWidth={RING_STROKE}
          strokeDasharray={`${(rate / 100) * RING_C} ${RING_C}`}
          transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
        />
      </svg>
      <div className="absolute left-[calc(50%+.5px)] top-[83px] flex w-[71px] -translate-x-1/2 flex-col items-center gap-[6px]">
        <p className="text-center text-[30px] font-semibold leading-[22px] tracking-[-.03em] tabular-nums text-[#111312]">
          {Math.round(rate)}%
        </p>
        <p className="whitespace-nowrap text-[16px] tracking-[-.03em] text-[#5E5A50]">Trefferquote</p>
      </div>
    </div>
  );
}

const INDICATORS = ["Anker Indikator", "Liquidations-Level", "Heatmap Indikator"];

function Indicators() {
  const active = useCycle(INDICATORS.length, 1700);

  return (
    <div className={cn(square, "rounded-[14.892px] bg-[#FFFAF0]")}>
      <p className="absolute left-[16px] top-[17px] whitespace-nowrap text-[14px] text-black">Trademania Indikatoren</p>
      {INDICATORS.map((text, i) => {
        const on = i === active;
        return (
          <div
            key={text}
            className={cn(
              "absolute left-[11px] flex h-[44px] w-[184px] items-center gap-[6px] rounded-[8px] px-[12px] transition-all duration-500 ease-[cubic-bezier(.22,1,.36,1)]",
              on ? "bg-white shadow-[0_6px_16px_-8px_rgba(17,19,18,.35)]" : "bg-[#F0EBE1]",
            )}
            style={{ top: 44 + i * 51 }}
          >
            <Svg src={eye} w={14} className={cn(on && "tm2-blink")} />
            <p className="whitespace-nowrap text-[11px] font-medium text-[#111312]">{text}</p>
            <span
              className={cn(
                "ml-auto rounded-[4px] bg-[#111312] px-[6px] py-[3px] text-[10px] font-medium leading-none text-[#D9F9B9] transition-all duration-500",
                on ? "translate-x-0 opacity-100" : "translate-x-[6px] opacity-0",
              )}
            >
              AN
            </span>
          </div>
        );
      })}
    </div>
  );
}

const PROFIT_STATES = [
  { factor: 1.5, win: 63 },
  { factor: 1.9, win: 69 },
  { factor: 1.3, win: 58 },
  { factor: 2.2, win: 72 },
];

function Profit() {
  const state = PROFIT_STATES[useCycle(PROFIT_STATES.length, 2800)];
  const factor = useTween(state.factor, 1100);
  const win = useTween(state.win, 1100);

  return (
    <div className={cn(square, "rounded-[7px] border border-white/10 bg-white")}>
      <p className="absolute left-[17px] top-[14px] whitespace-nowrap text-[14px] leading-[18px] text-black/60 opacity-90">
        Journal · Profitfaktor
      </p>
      <p className="absolute left-[17px] top-[40px] whitespace-nowrap text-[50px] font-semibold leading-[47px] tracking-[-.05em] tabular-nums text-black">
        {factor.toFixed(1)}’
      </p>
      <div className="absolute inset-x-[17px] top-[95px] flex flex-col gap-[4px] text-[16px] font-medium leading-[12px]">
        <div className="relative flex h-[44px] w-full items-center gap-[6px] overflow-hidden rounded-[8px] border border-black/[.04] bg-[#F7F7F7] px-[12px]">
          <span className="absolute inset-y-0 left-0 bg-[#549612]/10" style={{ width: `${win}%` }} />
          <span className="relative text-[#3B6C0B] opacity-70">GEWINN</span>
          <span className="relative tabular-nums text-[#549612]">{Math.round(win)}%</span>
        </div>
        <div className="relative flex h-[44px] w-full items-center gap-[6px] overflow-hidden rounded-[8px] border border-black/[.04] bg-[#F7F7F7] px-[12px] text-[#B81936]">
          <span className="absolute inset-y-0 left-0 bg-[#B81936]/[.08]" style={{ width: `${100 - win}%` }} />
          <span className="relative tabular-nums">{100 - Math.round(win)}%</span>
          <span className="relative opacity-70">VERLUST</span>
        </div>
      </div>
    </div>
  );
}

// the trade plays out with the candles: PnL climbs while they print, then the loop restarts
const TRADE_PNL = [0, 61.4, 152.07, 243.89, 243.89];

function Trade() {
  const pnl = useTween(TRADE_PNL[useCycle(TRADE_PNL.length, 1600, 3)], 1200);

  return (
    <div className={cn(square, "rounded-[15px] bg-[#1D1E1C]")}>
      <div className="absolute left-[12px] top-[14px] flex h-[20px] w-[182px] items-center justify-between whitespace-nowrap font-medium">
        <p className="text-[14px] text-white">btcusd</p>
        <p className="text-[12px] text-white/50">Long</p>
      </div>
      <div className="absolute left-[12px] top-[67px]">
        <Candles candles={TRADE_CANDLES} height={70} grid={[18, 35, 53]} gridColor="rgba(255,255,255,.06)" step={90} />
        <span className="tm2-line absolute left-0 top-[18px] h-px w-[182px] origin-left bg-[#DD8A3A]/80" />
        <span className="tm2-tag absolute left-[4px] top-[9px] whitespace-nowrap rounded-[3px] bg-[#DD8A3A] px-[5px] py-[2px] text-[12px] font-medium leading-[16px] text-[#0B0C0D]">
          ENTRY 58,640
        </span>
        <span className="tm2-line absolute left-0 top-[52px] h-px w-[182px] origin-left bg-[#FF5C6C]/80 [animation-delay:.35s]" />
        <span className="tm2-tag absolute left-[4px] top-[43px] whitespace-nowrap rounded-[3px] bg-[#FF5C6C] px-[5px] py-[2px] text-[12px] font-medium leading-[16px] text-[#0B0C0D] [animation-delay:.35s]">
          STOP 58,408
        </span>
      </div>
      <div className="absolute left-[12px] top-[170px] flex h-[24px] w-[182px] items-center justify-between whitespace-nowrap text-[12px]">
        <p className="text-white/[.55]">Realisierter PnL</p>
        <span className="tm2-trim-start tm2-trim-end rounded-[4px] bg-[#163222] px-[6px] pt-[5px] pb-[9px] font-medium leading-[16px] tabular-nums text-[#3BE07A]">
          +${pnl.toFixed(2).replace(".", ",")}
        </span>
      </div>
    </div>
  );
}

const WATCHLIST = [
  {
    name: "BTC",
    prices: [64688, 64731, 64654, 64702],
    changes: [0.39, 0.46, 0.34, 0.41],
    format: (n: number) => thousands(n, ","),
    path: "M0 11.78L1.798 10.354L3.658 11.098L5.456 11.656L7.316 10.23L9.114 11.284L10.974 9.424L12.772 7.998L14.632 7.13L16.43 6.014L18.29 5.518L20.088 5.332L21.948 3.596L23.746 3.038L25.606 1.302L27.404 1.798L29.264 0.744L31.062 0.62H32.922L34.72 1.426",
  },
  {
    name: "ETH",
    prices: [2481, 2474, 2486, 2469],
    changes: [-1.12, -1.38, -0.92, -1.57],
    format: (n: number) => thousands(n, "."),
    path: "M0 1.674L1.798 3.162L3.658 1.922L5.456 0.992L7.316 2.108L9.114 1.736L10.974 0.62L12.772 2.604L14.632 3.782L16.43 2.48L18.29 4.96L20.088 6.82L21.948 7.688L23.746 10.044L25.606 9.672L27.404 8.432L29.264 10.168L31.062 11.78L32.922 10.354L34.72 9.92",
  },
  {
    name: "SOL",
    prices: [148.2, 148.94, 147.71, 149.36],
    changes: [2.41, 2.92, 2.07, 3.21],
    format: (n: number) => n.toFixed(2).replace(".", ","),
    path: "M0 10.168L1.798 11.222L3.658 10.354L5.456 11.78L7.316 10.292L9.114 9.548L10.974 8.246L12.772 9.238L14.632 9.548L16.43 8.37L18.29 6.324L20.088 5.766L21.948 5.084L23.746 3.72L25.606 2.604L27.404 1.364L29.264 0.62L31.062 1.736L32.922 3.038L34.72 2.232",
  },
];

function WatchlistRow({ row, index, tick }: { row: (typeof WATCHLIST)[number]; index: number; tick: number }) {
  // rows update one after the other so the list reads as a live feed
  const step = Math.floor((tick + (2 - index)) / 3) % row.prices.length;
  const price = useTween(row.prices[step], 700);
  const change = useTween(row.changes[step], 700);
  const up = row.changes[0] >= 0;

  return (
    <div className="flex h-[40px] w-[182px] items-center gap-[8px] rounded-[8px] bg-[#E3F2D4] p-[8px] text-[12px] leading-[16px]">
      <div className="flex min-w-[1px] flex-1 flex-col whitespace-nowrap">
        <p className="font-semibold text-[#0B0C0D]">{row.name}</p>
        <p className="tabular-nums text-[#0B0C0D]/[.55]">{row.format(price)}</p>
      </div>
      <svg width={56} height={20} viewBox="0 0 34.72 12.4" fill="none" className="block shrink-0 overflow-visible">
        <path
          d={row.path}
          pathLength={1}
          stroke={up ? "#2E9D4E" : "#E0475A"}
          strokeWidth={0.93}
          className="tm2-draw"
          style={{ animationDelay: `${index * 0.45}s` }}
        />
      </svg>
      <p className={cn("whitespace-nowrap font-medium tabular-nums", up ? "text-[#2E7D1E]" : "text-[#C2384A]")}>
        {signed(change)}%
      </p>
    </div>
  );
}

function Watchlist() {
  const tick = useCycle(1200, 900);

  return (
    <div className={cn(square, "flex flex-col justify-between gap-[10px] rounded-[15px] bg-[#F2FFE5] px-[12px] pb-[12px] pt-[14px]")}>
      <p className="flex items-center gap-[6px] whitespace-nowrap text-[14px] font-medium leading-[17px] text-[#0B0C0D]">
        Watchlist
        <span className="tm2-live-dot h-[5px] w-[5px] rounded-full bg-[#2E9D4E]" />
      </p>
      {WATCHLIST.map((row, i) => (
        <WatchlistRow key={row.name} row={row} index={i} tick={tick} />
      ))}
    </div>
  );
}

function Chat() {
  return (
    <div className={cn(square, "rounded-[12px] bg-[#F2F2F2]/10")}>
      {/* 7s loop: the prompt pops in, the assistant starts typing, everything clears and repeats */}
      <div className="tm2-chat-msg absolute left-[25px] top-[18px] flex origin-top-right items-center justify-center gap-[6.038px] rounded-[14px] rounded-tr-[4px] bg-white/5 px-[8.05px] py-[6.038px]">
        <Svg src={chatIdea} w={16.076} />
        <p className="whitespace-nowrap text-[14px] text-white opacity-80">Analysiere den Chart</p>
      </div>
      <div className="tm2-chat-reply absolute left-[12px] top-[78px] flex origin-left items-center gap-[6px]">
        <Svg src={chatLogo} w={26} />
        <span className="flex h-[30px] w-[58px] items-center justify-center gap-[5px] rounded-[14px] rounded-tl-[4px] bg-white/5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="tm2-dot h-[6px] w-[6px] rounded-full bg-white"
              style={{ animationDelay: `${i * 0.16}s` }}
            />
          ))}
        </span>
      </div>
      <div className="absolute left-[10px] top-[162.44px] flex w-[186px] items-center justify-between rounded-[5.594px] bg-white/5 p-[5.594px]">
        <div className="flex items-center gap-[5.594px]">
          <Svg src={chatAdd} w={16.782} />
          <p className="whitespace-nowrap text-[12px] text-white opacity-50">Frag zum Trading</p>
          <span className="tm2-caret -ml-[3px] h-[13px] w-px bg-white/70" />
        </div>
        <div className="flex items-center gap-[5.594px]">
          <Svg src={chatMic} w={16.782} />
          <div className="tm2-send flex h-[22.376px] w-[22.376px] items-center justify-center rounded-full bg-[linear-gradient(180deg,#4A6C28_0%,#D9F9B9_135.94%)]">
            <Svg src={chatArrow} w={11.188} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- positions */

type TileSpec = { key: string; x: number; y: number; w: number; h: number; Tile: () => React.JSX.Element };

/** Ein System tiles with their Figma frame positions (1920 × 1216 frame). */
export const SYSTEM_TILES: TileSpec[] = [
  { key: "watchlist", x: 857, y: 932, w: 206, h: 206, Tile: Watchlist },
  { key: "ai", x: 852, y: 91, w: 206, h: 206, Tile: AiChat },
  { key: "win", x: 331, y: 514, w: 206, h: 206, Tile: WinRate },
  { key: "indicators", x: 1221, y: 194, w: 206, h: 206, Tile: Indicators },
  { key: "btc-usd", x: 497, y: 186, w: 206, h: 206, Tile: BtcUsdChart },
  { key: "trade", x: 521, y: 806, w: 206, h: 206, Tile: Trade },
  { key: "chat", x: 1181, y: 813, w: 206, h: 206, Tile: Chat },
  { key: "profit", x: 1387, y: 501, w: 206, h: 206, Tile: Profit },
];

/**
 * Phone ring (Figma "LP Mobile" · Ein System 12818:174252, 430 × 941): full-size 206px tiles on
 * one wide ellipse around the copy, a row of four above it (row 0) and four below (row 1), 232px
 * apart (`x` = the tile's left edge in the Figma frame; `y` follows from the ellipse). Three of each
 * row are on screen; the fourth waits off-screen to come round next (Figma parks "Journal · All
 * Trades" on the right, where the bottom row comes in).
 */
export const SYSTEM_TILES_MOBILE: { key: string; x: number; row: 0 | 1; Tile: () => React.JSX.Element }[] = [
  { key: "watchlist", x: -120, row: 0, Tile: Watchlist },
  { key: "ai", x: 112, row: 0, Tile: AiChat },
  { key: "chat", x: 344, row: 0, Tile: Chat },
  { key: "profit", x: 576, row: 0, Tile: Profit },
  { key: "indicators", x: -120, row: 1, Tile: Indicators },
  { key: "btc-usd", x: 112, row: 1, Tile: BtcUsdChart },
  { key: "win", x: 344, row: 1, Tile: WinRate },
  { key: "trade", x: 576, row: 1, Tile: Trade },
];
